import type { ReactNode } from "react";
import { Button, Empty, Spin, Tag, Tooltip } from "antd";
import { CloseOutlined, CopyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Invitation } from "../types";
import { TeamRoleLabel } from "./TeamRoleLabel";
import styles from "./InvitationsTable.module.css";

export interface InvitationsTableProps {
  invitations: Invitation[];
  loading: boolean;
  error: boolean;
  /** AHA-43204: free teams hide the Licence column — there's nothing to
   *  auto-grant on accept, so the intent badge and "no licence" placeholder
   *  would render as pure noise. Mirrors the same gate on MembersTable. */
  isPaidTeamPlan: boolean;
  onCopyLink: (invitation: Invitation) => void;
  onWithdraw: (invitation: Invitation) => void;
  /** Toolbar content rendered inside the table card, above the column
   *  header and divided from it by a border (e.g. the Export button). */
  header?: ReactNode;
}

export function InvitationsTable({
  invitations,
  loading,
  error,
  isPaidTeamPlan,
  onCopyLink,
  onWithdraw,
  header,
}: InvitationsTableProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <p style={{ color: "#d4380d", textAlign: "center", marginTop: 16 }}>
        {t("team.toast.genericError")}
      </p>
    );
  }

  if (!invitations.length) {
    return <Empty description={t("team.empty.invitations")} />;
  }

  return (
    <div
      data-testid="team-management-invitations-table"
      style={{
        overflow: "auto",
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        background: "#fff",
      }}
    >
      {header && <div style={headerRow}>{header}</div>}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          // Shrink the horizontal-scroll min-width by one column-worth when
          // the Licence column drops out on free teams, so the remaining
          // columns don't get visually stretched on mid-width viewports.
          minWidth: isPaidTeamPlan ? 960 : 820,
        }}
      >
        <thead>
          <tr>
            <th style={th}>{t("team.column.email")}</th>
            <th style={th}>{t("team.column.invitationLink")}</th>
            <th style={th}>{t("team.column.role")}</th>
            {isPaidTeamPlan && <th style={th}>{t("team.column.license")}</th>}
            <th style={th}>{t("team.column.status")}</th>
            <th style={th} />
          </tr>
        </thead>
        <tbody>
          {invitations.map((invitation, idx) => {
            const isLast = idx === invitations.length - 1;
            const cellStyle = isLast ? td : { ...td, borderBottom: "1px solid #f0f0f0" };
            return (
              <tr key={invitation.id}>
                <td style={cellStyle}>
                  <a href={`mailto:${invitation.invitedEmail}`}>{invitation.invitedEmail}</a>
                </td>
                <td style={cellStyle}>
                  {/* `<button>` instead of `<td onClick>` so keyboard
                      users can tab into it and the cell shows clear
                      hover/focus feedback (AHA-42787 #2). */}
                  <Tooltip title={t("team.action.copy")}>
                    <button
                      type="button"
                      className={styles.copyLinkCell}
                      onClick={() => onCopyLink(invitation)}
                    >
                      <span>{invitation.link}</span>
                      <CopyOutlined />
                    </button>
                  </Tooltip>
                </td>
                <td style={cellStyle}>
                  <TeamRoleLabel role={invitation.accountRole} />
                </td>
                {/* AHA-41755: shows whether this invitee will be
                    auto-granted a V2 licence on accept. Sourced from the
                    team-mgmt intent table (team_invitation_intents).
                    Subject to the cap at accept time — first-come-first-
                    served, no extra UX needed here.
                    AHA-43204: hidden on free teams (no licences to grant). */}
                {isPaidTeamPlan && (
                  <td style={cellStyle}>
                    {invitation.assignLicenseOnAccept ? (
                      <Tag
                        color="blue"
                        style={{ marginInlineEnd: 0 }}
                        data-testid="my-team-invitation-assign-license-badge"
                      >
                        {t("team.license.willAssignOnAccept")}
                      </Tag>
                    ) : (
                      <span style={{ color: "var(--ant-color-text-tertiary)" }}>—</span>
                    )}
                  </td>
                )}
                <td style={{ ...cellStyle, textTransform: "capitalize" }}>
                  {t(`team.status.${invitation.status}`, invitation.status)}
                </td>
                <td style={cellStyle}>
                  {invitation.status === "pending" && (
                    <Tooltip title={t("team.action.withdrawInvitation")}>
                      <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={() => onWithdraw(invitation)}
                        data-testid="my-team-invitation-withdraw-button"
                      />
                    </Tooltip>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// In-card toolbar row (e.g. Export) sitting above the column header,
// divided from it by the same border the header cells use.
const headerRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: 16,
  borderBottom: "1px solid #f0f0f0",
};

const th: React.CSSProperties = {
  padding: 16,
  textAlign: "left",
  fontSize: 12,
  fontWeight: 600,
  color: "#717171",
  borderBottom: "1px solid #f0f0f0",
};

const td: React.CSSProperties = {
  padding: 16,
  fontSize: 14,
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};
